import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { createPerson } from '../../services/person-api.js';
import type { CreatePersonRequest, Person } from '../../types/person.js';

const CATEGORIES = ['Familie', 'Geschaeftlich', 'Kunde', 'Freund', 'Bekannter'];

const RELATIONSHIP_TYPES = [
  { value: 'personal', label: 'Persoenlich' },
  { value: 'business', label: 'Geschaeftlich' },
  { value: 'family', label: 'Familie' },
  { value: 'friend', label: 'Freund' },
  { value: 'acquaintance', label: 'Bekannter' },
];

@customElement('cp-person-form')
export class CpPersonForm extends LitElement {
  createRenderRoot() { return this; }

  @state() private _open = false;
  @state() private _fullName = '';
  @state() private _nickname = '';
  @state() private _category = '';
  @state() private _relationshipType = '';
  @state() private _email = '';
  @state() private _phone = '';
  @state() private _birthday = '';
  @state() private _subtitle = '';
  @state() private _submitting = false;
  @state() private _error = '';
  @state() private _nameError = '';

  connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('keydown', this._onKeyDown);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._onKeyDown);
  }

  open(): void {
    this._resetForm();
    this._open = true;
  }

  private _resetForm(): void {
    this._fullName = '';
    this._nickname = '';
    this._category = '';
    this._relationshipType = '';
    this._email = '';
    this._phone = '';
    this._birthday = '';
    this._subtitle = '';
    this._submitting = false;
    this._error = '';
    this._nameError = '';
  }

  private _close(): void {
    this._open = false;
  }

  private _onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this._open) this._close();
  };

  private _onBackdropClick(e: Event): void {
    if ((e.target as HTMLElement).classList.contains('cp-person-form__overlay')) {
      this._close();
    }
  }

  private _onNameInput(e: Event): void {
    this._fullName = (e.target as HTMLInputElement).value;
    if (this._nameError) this._nameError = '';
  }

  private async _onSubmit(): Promise<void> {
    const name = this._fullName.trim();
    if (!name) {
      this._nameError = 'Name ist erforderlich';
      return;
    }

    this._submitting = true;
    this._error = '';

    try {
      const data: CreatePersonRequest = { full_name: name };
      if (this._nickname.trim()) data.nickname = this._nickname.trim();
      if (this._category) data.category = this._category;
      if (this._relationshipType) data.relationship_type = this._relationshipType;
      if (this._email.trim()) data.email = this._email.trim();
      if (this._phone.trim()) data.phone = this._phone.trim();
      if (this._birthday) data.birthday = this._birthday;
      if (this._subtitle.trim()) data.subtitle = this._subtitle.trim();

      const person = await createPerson(data);
      this._open = false;
      this.dispatchEvent(new CustomEvent<{ person: Person }>('person-created', {
        detail: { person },
        bubbles: true,
        composed: true,
      }));
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Person konnte nicht erstellt werden';
    } finally {
      this._submitting = false;
    }
  }

  render() {
    if (!this._open) return nothing;

    return html`
      <div class="cp-person-form__overlay" @click=${this._onBackdropClick}>
        <div class="cp-person-form__dialog">
          <div class="cp-person-form__header">
            <h2 class="cp-person-form__title">Neue Person anlegen</h2>
            <button class="cp-person-form__close" @click=${this._close}>&times;</button>
          </div>

          <div class="cp-person-form__body">
            <div class="cp-person-form__field">
              <label class="cp-person-form__label">Name *</label>
              <input
                class="cp-person-form__input${this._nameError ? ' cp-person-form__input--error' : ''}"
                type="text"
                placeholder="Vor- und Nachname"
                .value=${this._fullName}
                @input=${this._onNameInput}
                ?disabled=${this._submitting}
                autocomplete="off"
              />
              ${this._nameError ? html`<p class="cp-person-form__field-error">${this._nameError}</p>` : nothing}
            </div>

            <div class="cp-person-form__row">
              <div class="cp-person-form__field">
                <label class="cp-person-form__label">Rufname</label>
                <input
                  class="cp-person-form__input"
                  type="text"
                  placeholder="Spitzname"
                  .value=${this._nickname}
                  @input=${(e: Event) => { this._nickname = (e.target as HTMLInputElement).value; }}
                  ?disabled=${this._submitting}
                />
              </div>

              <div class="cp-person-form__field">
                <label class="cp-person-form__label">Kategorie</label>
                <select
                  class="cp-person-form__select"
                  @change=${(e: Event) => { this._category = (e.target as HTMLSelectElement).value; }}
                  ?disabled=${this._submitting}
                >
                  <option value="">Keine Kategorie</option>
                  ${CATEGORIES.map(cat => html`
                    <option value=${cat} ?selected=${this._category === cat}>${cat}</option>
                  `)}
                </select>
              </div>
            </div>

            <div class="cp-person-form__row">
              <div class="cp-person-form__field">
                <label class="cp-person-form__label">Beziehungstyp</label>
                <select
                  class="cp-person-form__select"
                  @change=${(e: Event) => { this._relationshipType = (e.target as HTMLSelectElement).value; }}
                  ?disabled=${this._submitting}
                >
                  <option value="">Kein Typ</option>
                  ${RELATIONSHIP_TYPES.map(rt => html`
                    <option value=${rt.value} ?selected=${this._relationshipType === rt.value}>${rt.label}</option>
                  `)}
                </select>
              </div>

              <div class="cp-person-form__field">
                <label class="cp-person-form__label">Subtitle</label>
                <input
                  class="cp-person-form__input"
                  type="text"
                  placeholder="z.B. Steuerberater"
                  .value=${this._subtitle}
                  @input=${(e: Event) => { this._subtitle = (e.target as HTMLInputElement).value; }}
                  ?disabled=${this._submitting}
                />
              </div>
            </div>

            <div class="cp-person-form__row">
              <div class="cp-person-form__field">
                <label class="cp-person-form__label">E-Mail</label>
                <input
                  class="cp-person-form__input"
                  type="email"
                  placeholder="email@example.com"
                  .value=${this._email}
                  @input=${(e: Event) => { this._email = (e.target as HTMLInputElement).value; }}
                  ?disabled=${this._submitting}
                />
              </div>

              <div class="cp-person-form__field">
                <label class="cp-person-form__label">Telefon</label>
                <input
                  class="cp-person-form__input"
                  type="tel"
                  placeholder="+49 123 456"
                  .value=${this._phone}
                  @input=${(e: Event) => { this._phone = (e.target as HTMLInputElement).value; }}
                  ?disabled=${this._submitting}
                />
              </div>
            </div>

            <div class="cp-person-form__field">
              <label class="cp-person-form__label">Geburtstag</label>
              <input
                class="cp-person-form__input"
                type="date"
                .value=${this._birthday}
                @input=${(e: Event) => { this._birthday = (e.target as HTMLInputElement).value; }}
                ?disabled=${this._submitting}
              />
            </div>

            ${this._error ? html`
              <div class="cp-person-form__error">${this._error}</div>
            ` : nothing}
          </div>

          <div class="cp-person-form__footer">
            <button
              class="cp-person-form__btn cp-person-form__btn--cancel"
              @click=${this._close}
              ?disabled=${this._submitting}
            >Abbrechen</button>
            <button
              class="cp-person-form__btn cp-person-form__btn--submit"
              @click=${this._onSubmit}
              ?disabled=${this._submitting}
            >${this._submitting ? 'Wird erstellt...' : 'Person anlegen'}</button>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-person-form': CpPersonForm;
  }
}
